import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp,
  Visibility,
  TouchApp,
  Person,
  LocationOn,
  Assessment,
  Timeline,
  Compare,
  Refresh,
  Campaign,
  AttachMoney,
  Group
} from '@mui/icons-material';

const AdAnalytics = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [compareMode, setCompareMode] = useState(false);
  const [compareRange, setCompareRange] = useState('previous');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [analyticsData, setAnalyticsData] = useState(null);

  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange, compareMode, compareRange]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      // Mock data - replace with real API call
      const mockData = {
        overview: {
          totalSpent: 1250.50,
          totalImpressions: 45000,
          totalClicks: 3200,
          totalEngagements: 8900,
          avgCPC: 0.39,
          avgCPM: 27.78,
          avgCTR: 7.11,
          avgEngagementRate: 19.78,
          // Add budget management data
          dailySpend: 45.20,
          dailyLimit: 100,
          monthlySpend: 1250.50,
          monthlyBudget: 2000,
          weeklySpend: 320.80,
          weeklyLimit: 500,
          // Add ROI data
          totalRevenue: 8750.00,
          roas: 7.0,
          cpa: 25.00,
          ltv: 500.00
        },
        performance: {
          daily: [
            { date: '2024-08-01', impressions: 3200, clicks: 240, spend: 95.20, ctr: 7.5 },
            { date: '2024-08-02', impressions: 4100, clicks: 310, spend: 118.50, ctr: 7.6 },
            { date: '2024-08-03', impressions: 3800, clicks: 290, spend: 112.30, ctr: 7.6 },
            { date: '2024-08-04', impressions: 5200, clicks: 420, spend: 158.70, ctr: 8.1 },
            { date: '2024-08-05', impressions: 4800, clicks: 380, spend: 145.20, ctr: 7.9 },
            { date: '2024-08-06', impressions: 6100, clicks: 520, spend: 198.40, ctr: 8.5 },
            { date: '2024-08-07', impressions: 5500, clicks: 450, spend: 175.30, ctr: 8.2 }
          ]
        },
        demographics: {
          age: [
            { range: '18-24', percentage: 25, impressions: 11250 },
            { range: '25-34', percentage: 35, impressions: 15750 },
            { range: '35-44', percentage: 22, impressions: 9900 },
            { range: '45-54', percentage: 12, impressions: 5400 },
            { range: '55+', percentage: 6, impressions: 2700 }
          ],
          gender: [
            { gender: 'Male', percentage: 48, impressions: 21600 },
            { gender: 'Female', percentage: 52, impressions: 23400 }
          ]
        },
        geography: {
          states: [
            { state: 'California', impressions: 8500, percentage: 18.9 },
            { state: 'Texas', impressions: 7200, percentage: 16.0 },
            { state: 'Florida', impressions: 6300, percentage: 14.0 },
            { state: 'New York', impressions: 5800, percentage: 12.9 },
            { state: 'Illinois', impressions: 4200, percentage: 9.3 },
            { state: 'Pennsylvania', impressions: 3800, percentage: 8.4 },
            { state: 'Ohio', impressions: 3200, percentage: 7.1 },
            { state: 'Georgia', impressions: 2800, percentage: 6.2 },
            { state: 'Michigan', impressions: 2400, percentage: 5.3 },
            { state: 'North Carolina', impressions: 2100, percentage: 4.7 }
          ]
        },
        campaigns: [
          {
            id: 1,
            name: 'Summer Sale Campaign',
            status: 'active',
            spend: 450.20,
            impressions: 16500,
            clicks: 1200,
            ctr: 7.3,
            cpc: 0.38,
            engagements: 3200
          },
          {
            id: 2,
            name: 'Product Launch Ads',
            status: 'active',
            spend: 380.50,
            impressions: 14200,
            clicks: 980,
            ctr: 6.9,
            cpc: 0.39,
            engagements: 2800
          },
          {
            id: 3,
            name: 'Brand Awareness',
            status: 'paused',
            spend: 420.80,
            impressions: 14300,
            clicks: 1020,
            ctr: 7.1,
            cpc: 0.41,
            engagements: 2900
          }
        ]
      };

      setAnalyticsData(mockData);
    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const getPerformanceColor = (value, threshold) => {
    return value >= threshold ? 'success' : value >= threshold * 0.8 ? 'warning' : 'error';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assessment color="primary" />
          Ad Analytics Dashboard
        </Typography>
        <Button
          variant="outlined"
          onClick={loadAnalyticsData}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <Refresh />}
        >
          Refresh Data
        </Button>
      </Box>

      {/* Time Range Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Time Range</InputLabel>
                <Select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                >
                  <MenuItem value="1h">Last Hour</MenuItem>
                  <MenuItem value="24h">Last 24 Hours</MenuItem>
                  <MenuItem value="7d">Last 7 Days</MenuItem>
                  <MenuItem value="30d">Last 30 Days</MenuItem>
                  <MenuItem value="90d">Last 90 Days</MenuItem>
                  <MenuItem value="1y">Last Year</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Compare With</InputLabel>
                <Select
                  value={compareMode}
                  onChange={(e) => setCompareMode(e.target.value)}
                >
                  <MenuItem value={false}>No Comparison</MenuItem>
                  <MenuItem value={true}>Previous Period</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {compareMode && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Compare Range</InputLabel>
                  <Select
                    value={compareRange}
                    onChange={(e) => setCompareRange(e.target.value)}
                  >
                    <MenuItem value="previous">Previous Period</MenuItem>
                    <MenuItem value="same_period_last_year">Same Period Last Year</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {analyticsData && (
        <>
          {/* Budget Management Dashboard */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Budget Management
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6">Daily Budget</Typography>
                  <Typography variant="h4" color="primary" gutterBottom>
                    {formatCurrency(analyticsData.overview.dailySpend)} / {formatCurrency(analyticsData.overview.dailyLimit)}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={(analyticsData.overview.dailySpend / analyticsData.overview.dailyLimit) * 100}
                    color={(analyticsData.overview.dailySpend / analyticsData.overview.dailyLimit) * 100 >= 90 ? 'error' : 'primary'}
                    sx={{ height: 8, borderRadius: 4, mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6">Monthly Budget</Typography>
                  <Typography variant="h4" color="primary" gutterBottom>
                    {formatCurrency(analyticsData.overview.monthlySpend)} / {formatCurrency(analyticsData.overview.monthlyBudget)}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={(analyticsData.overview.monthlySpend / analyticsData.overview.monthlyBudget) * 100}
                    color={(analyticsData.overview.monthlySpend / analyticsData.overview.monthlyBudget) * 100 >= 90 ? 'error' : 'primary'}
                    sx={{ height: 8, borderRadius: 4, mb: 2 }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* ROI Tracking */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                ROI Performance
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={6} md={3}>
                  <Typography variant="h4" color="success.main">
                    {formatCurrency(analyticsData.overview.totalRevenue)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Revenue
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="h4" color="success.main">
                    {analyticsData.overview.roas}x
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ROAS
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="h4" color="warning.main">
                    {formatCurrency(analyticsData.overview.cpa)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cost Per Acquisition
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="h4" color="success.main">
                    {formatCurrency(analyticsData.overview.ltv)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Customer LTV
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Overview Metrics */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AttachMoney color="primary" />
                    <Typography variant="h6">Total Spent</Typography>
                  </Box>
                  <Typography variant="h4" color="primary">
                    {formatCurrency(analyticsData.overview.totalSpent)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatNumber(analyticsData.overview.totalImpressions)} impressions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Visibility color="primary" />
                    <Typography variant="h6">Impressions</Typography>
                  </Box>
                  <Typography variant="h4" color="primary">
                    {formatNumber(analyticsData.overview.totalImpressions)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {analyticsData.overview.avgCPM.toFixed(2)} CPM
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TouchApp color="primary" />
                    <Typography variant="h6">Clicks</Typography>
                  </Box>
                  <Typography variant="h4" color="primary">
                    {formatNumber(analyticsData.overview.totalClicks)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {analyticsData.overview.avgCPC.toFixed(2)} CPC
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Group color="primary" />
                    <Typography variant="h6">Engagements</Typography>
                  </Box>
                  <Typography variant="h4" color="primary">
                    {formatNumber(analyticsData.overview.totalEngagements)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {analyticsData.overview.avgEngagementRate.toFixed(1)}% rate
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Performance Optimization */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Performance Optimization
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    AI Recommendations
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Alert severity="success" sx={{ py: 0 }}>
                      Increase budget for "Summer Sale Campaign" - performing 40% above average
                    </Alert>
                    <Alert severity="warning" sx={{ py: 0 }}>
                      Pause "Product Launch Ads" - CTR below 2% threshold
                    </Alert>
                    <Alert severity="info" sx={{ py: 0 }}>
                      Optimize targeting for "Lead Generation Campaign" - high CPA detected
                    </Alert>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Budget Optimization
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Alert severity="success" sx={{ py: 0 }}>
                      Daily budget can be increased by $25 based on performance
                    </Alert>
                    <Alert severity="info" sx={{ py: 0 }}>
                      Monthly budget utilization: 62.5% - room for scaling
                    </Alert>
                    <Alert severity="warning" sx={{ py: 0 }}>
                      Consider reducing spend on underperforming campaigns
                    </Alert>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Performance Metrics */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Click-Through Rate (CTR)
                  </Typography>
                  <Typography variant="h3" color="primary">
                    {analyticsData.overview.avgCTR.toFixed(2)}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={analyticsData.overview.avgCTR} 
                    color={getPerformanceColor(analyticsData.overview.avgCTR, 5)}
                    sx={{ mt: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Industry average: 2.35%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Cost Per Click (CPC)
                  </Typography>
                  <Typography variant="h3" color="primary">
                    ${analyticsData.overview.avgCPC.toFixed(2)}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={(analyticsData.overview.avgCPC / 2) * 100} 
                    color={getPerformanceColor(analyticsData.overview.avgCPC, 0.5)}
                    sx={{ mt: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Industry average: $0.35
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Engagement Rate
                  </Typography>
                  <Typography variant="h3" color="primary">
                    {analyticsData.overview.avgEngagementRate.toFixed(1)}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={analyticsData.overview.avgEngagementRate} 
                    color={getPerformanceColor(analyticsData.overview.avgEngagementRate, 15)}
                    sx={{ mt: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Industry average: 12.5%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Detailed Analytics Tabs */}
          <Card>
            <CardContent>
              <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
                <Tab label="Campaign Performance" />
                <Tab label="Demographics" />
                <Tab label="Geographic Data" />
                <Tab label="Daily Trends" />
              </Tabs>

              {/* Campaign Performance Tab */}
              {activeTab === 0 && (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Campaign Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Spend</TableCell>
                        <TableCell align="right">Impressions</TableCell>
                        <TableCell align="right">Clicks</TableCell>
                        <TableCell align="right">CTR</TableCell>
                        <TableCell align="right">CPC</TableCell>
                        <TableCell align="right">Engagements</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analyticsData.campaigns.map((campaign) => (
                        <TableRow key={campaign.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Campaign color="primary" />
                              {campaign.name}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={campaign.status} 
                              color={campaign.status === 'active' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">{formatCurrency(campaign.spend)}</TableCell>
                          <TableCell align="right">{formatNumber(campaign.impressions)}</TableCell>
                          <TableCell align="right">{formatNumber(campaign.clicks)}</TableCell>
                          <TableCell align="right">{campaign.ctr.toFixed(2)}%</TableCell>
                          <TableCell align="right">${campaign.cpc.toFixed(2)}</TableCell>
                          <TableCell align="right">{formatNumber(campaign.engagements)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Demographics Tab */}
              {activeTab === 1 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Age Distribution
                    </Typography>
                    {analyticsData.demographics.age.map((ageGroup) => (
                      <Box key={ageGroup.range} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">{ageGroup.range}</Typography>
                          <Typography variant="body2">{ageGroup.percentage}%</Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={ageGroup.percentage} 
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    ))}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Gender Distribution
                    </Typography>
                    {analyticsData.demographics.gender.map((gender) => (
                      <Box key={gender.gender} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">{gender.gender}</Typography>
                          <Typography variant="body2">{gender.percentage}%</Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={gender.percentage} 
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    ))}
                  </Grid>
                </Grid>
              )}

              {/* Geographic Data Tab */}
              {activeTab === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Top States by Impressions
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>State</TableCell>
                          <TableCell align="right">Impressions</TableCell>
                          <TableCell align="right">Percentage</TableCell>
                          <TableCell align="right">Performance</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analyticsData.geography.states.map((state) => (
                          <TableRow key={state.state}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LocationOn color="primary" />
                                {state.state}
                              </Box>
                            </TableCell>
                            <TableCell align="right">{formatNumber(state.impressions)}</TableCell>
                            <TableCell align="right">{state.percentage}%</TableCell>
                            <TableCell align="right">
                              <Chip 
                                label="Good" 
                                color="success" 
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Daily Trends Tab */}
              {activeTab === 3 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Daily Performance Trends
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell align="right">Impressions</TableCell>
                          <TableCell align="right">Clicks</TableCell>
                          <TableCell align="right">Spend</TableCell>
                          <TableCell align="right">CTR</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analyticsData.performance.daily.map((day) => (
                          <TableRow key={day.date}>
                            <TableCell>{new Date(day.date).toLocaleDateString()}</TableCell>
                            <TableCell align="right">{formatNumber(day.impressions)}</TableCell>
                            <TableCell align="right">{formatNumber(day.clicks)}</TableCell>
                            <TableCell align="right">{formatCurrency(day.spend)}</TableCell>
                            <TableCell align="right">{day.ctr.toFixed(2)}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default AdAnalytics; 